/**
 * ULD历史记录表格组件
 * 以时间线形式展示ULD的完整变更历史
 */

import React, { useState } from 'react';
import {
    Table,
    Tag,
    Space,
    Button,
    Tooltip,
    Typography,
    Modal,
    Descriptions,
    Card,
    Empty,
    ConfigProvider,
} from 'antd';
import { 
    EyeOutlined, 
    ClockCircleOutlined,
    UserOutlined,
    EnvironmentOutlined,
    FlagOutlined,
    SettingOutlined,
    ExportOutlined,
    ImportOutlined,
} from '@ant-design/icons';

import {
    ULD_EVENT_TYPE_LABELS,
    ULD_EVENT_TYPE_COLORS,
    ULD_STATUS_LABELS,
    ULD_STATUS_COLORS,
    ULD_LOCATION_TYPE_LABELS,
} from '../constants/historyConstants';

const { Text } = Typography;

const UldHistoryTable = ({ 
    data, 
    loading, 
    pagination, 
    onPageChange,
    onShowSizeChange,
    // —— 复用化开关（默认全关，独立历史页行为零回归）——
    embedded = false,          // 内嵌模式：去掉整页高度计算/sticky，适配弹窗/抽屉里嵌入
    hiddenColumns = [],        // 隐藏的列 key（如单板历史里隐藏冗余的「ULD识别码」列）
    onUcmLink = null,          // 传入则「批次标识」列对 UCM 批次(UCM-前缀)渲染成可点链接，回调 batch_identifier
    onUldClick = null,         // 传入则「操作」列加「查看板」按钮，回调整行 record（用于 UCM 详情点回板详情）
    scrollY,                   // 内嵌时可自定义表体高度；不传则不限高
}) => {
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [detailModalVisible, setDetailModalVisible] = useState(false);

    // 显示详情模态框
    const showDetailModal = (record) => {
        setSelectedRecord(record);
        setDetailModalVisible(true);
    };

    // 关闭详情模态框
    const closeDetailModal = () => {
        setDetailModalVisible(false);
        setSelectedRecord(null);
    };

    // 格式化事件类型标签
    const renderEventType = (eventType) => {
        const label = ULD_EVENT_TYPE_LABELS[eventType];
        const color = ULD_EVENT_TYPE_COLORS[eventType];
        
        if (label && color) {
            // 已知的事件类型，显示中文标签
            return (
                <Tag color={color} style={{ borderRadius: '4px', fontSize: '11px', padding: '2px 6px' }}>
                    {label}
                </Tag>
            );
        } else {
            // 未知的事件类型，显示原始值并添加提示
            return (
                <Tooltip title={`未知事件类型: ${eventType}`} placement="topLeft">
                    <Tag color="#666666" style={{ borderRadius: '4px', fontSize: '11px', padding: '2px 6px', border: '1px dashed #d9d9d9' }}>
                        {eventType}
                    </Tag>
                </Tooltip>
            );
        }
    };

    // 格式化时间
    const formatDateTime = (dateTime) => {
        if (!dateTime) return '-';
        const date = new Date(dateTime);
        return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}`;
    };

    // 格式化通用变更内容，提供更可读的输出
    const formatGenericChangeContent = (eventType, previousValue, newValue, record) => {
        if (!newValue) return "";
        
        // 定义字段的中文映射
        const fieldMapping = {
            'owner': '所有者',
            'holder': '持有者', 
            'status': '状态',
            'core_status': '核心状态',
            'availability': '可用性',
            'location_type': '位置类型',
            'airport_iata': '机场代码',
            'transport_identifier': '运输标识',
            'gha': '地面处理代理',
            'ownership_type': '所有权类型',
            'tags': '标签',
            'remarks': '备注',
            'uld_type': 'ULD类型',
            'current_location_type': '位置类型',
            'current_airport_iata': '机场代码',
            'current_status': '状态',
            'current_flight_op_id': '关联航班',
            'flight_op_id': '关联航班',
            'other_location': '其他位置',
            // 新增：外放相关字段映射
            'delivered_to_consignee': '外放状态',
            'consignee_name': '收货人',
        };
        
        // 格式化显示值的函数
        const formatDisplayValue = (key, value, record) => {
            // 处理null值和undefined
            if (value === null || value === undefined) {
                return '空';
            }
            
            // 处理空字符串
            if (value === '') {
                return '空';
            }
            
            // 处理航班ID - 显示航班号而不是ID
            if ((key === 'flight_op_id' || key === 'current_flight_op_id') && value && record) {
                // 如果当前记录的flight_op_id与要显示的值匹配，且有flight_number信息，优先显示航班号
                if (record.flight_op_id === value && record.flight_number) {
                    return record.flight_number;
                }
                // 如果是变更类型且涉及航班，尝试从transport_identifier中获取信息
                if (record.transport_identifier && (record.event_type === 'LOADED' || record.event_type === 'UNLOADED' || record.event_type === 'LOC_CHANGE')) {
                    return record.transport_identifier;
                }
                // 否则显示格式化的ID
                return `航班#${value}`;
            }
            
            // 处理位置类型
            if (key === 'location_type' || key === 'current_location_type') {
                return locationTypeMapping[value] || value;
            }
            
            // 处理核心状态
            if (key === 'core_status') {
                return coreStatusMapping[value] || value;
            }
            
            // 处理可用性
            if (key === 'availability') {
                return availabilityMapping[value] || value;
            }
            
            // 处理所有权类型
            if (key === 'ownership_type') {
                return ownershipMapping[value] || value;
            }
            
            // 处理标签数组
            if (key === 'tags' && Array.isArray(value)) {
                return value.length > 0 ? value.join(', ') : '无';
            }
            
            // 处理外放状态
            if (key === 'delivered_to_consignee') {
                if (value === true || value === 'true' || value === 1 || value === '1') {
                    return '已外放';
                } else if (value === false || value === 'false' || value === 0 || value === '0') {
                    return '未外放';
                } else {
                    return String(value);
                }
            }
            
            // 处理收货人 - 如果为空则显示友好信息
            if (key === 'consignee_name') {
                return value || '无收货人';
            }
            
            // 处理其他值
            return value;
        };
        
        // 位置类型映射
        const locationTypeMapping = {
            'AIRPORT': '机场',
            'FLIGHT': '航班', 
            'OTHER_LOCATION': '其他位置'
        };
        
        // 核心状态映射
        const coreStatusMapping = {
            'IN_STOCK': '在库',
            'IN_TRANSIT': '在途',
            'OUT_OF_SERVICE': '停用',
            'RETIRED': '退役',
            'LOST': '丢失'
        };
        
        // 可用性映射
        const availabilityMapping = {
            'AVAILABLE': '可用',
            'OCCUPIED': '占用', 
            'UNSERVICEABLE': '不可服务'
        };
        
        // 所有权类型映射
        const ownershipMapping = {
            '自有': '自有',
            '租赁': '租赁',
            'OWNED': '自有',
            'LEASED': '租赁'
        };
        
        // 提取有意义的变更信息
        const changes = [];
        
        // 如果有前值，显示对比
        if (previousValue && typeof previousValue === 'object') {
            // 处理new_value中的字段
            for (const [key, newVal] of Object.entries(newValue)) {
                if (key in previousValue) {
                    const prevVal = previousValue[key];
                    // 对比时考虑null值的情况
                    if (prevVal !== newVal) {
                        // 统一使用格式化函数处理显示值
                        const prevDisplay = formatDisplayValue(key, prevVal, record);
                        const newDisplay = formatDisplayValue(key, newVal, record);
                        const fieldName = fieldMapping[key] || key;
                        changes.push(`${fieldName}: ${prevDisplay} → ${newDisplay}`);
                    }
                } else if (newVal !== null && newVal !== undefined && newVal !== '') {
                    // 新增字段（只有当值不为空时才显示）
                    const fieldName = fieldMapping[key] || key;
                    const displayValue = formatDisplayValue(key, newVal, record);
                    changes.push(`新增${fieldName}: ${displayValue}`);
                }
            }
            
            // 处理previous_value中有但new_value中没有的字段（被删除的字段）
            for (const [key, prevVal] of Object.entries(previousValue)) {
                if (!(key in newValue) && prevVal !== null && prevVal !== undefined && prevVal !== '') {
                    const fieldName = fieldMapping[key] || key;
                    const prevDisplay = formatDisplayValue(key, prevVal, record);
                    changes.push(`删除${fieldName}: ${prevDisplay}`);
                }
            }
        } else {
            // 没有前值，显示所有当前值
            for (const [key, newVal] of Object.entries(newValue)) {
                // 只有当值不为null、空字符串，或者是非空数组时才显示
                if (newVal !== null && newVal !== "" && (!Array.isArray(newVal) || (Array.isArray(newVal) && newVal.length > 0))) {
                    const fieldName = fieldMapping[key] || key;
                    const displayValue = formatDisplayValue(key, newVal, record);
                    changes.push(`${fieldName}: ${displayValue}`);
                }
            }
        }
        
        // 限制显示长度，避免过长
        const result = changes.join('; ');
        if (result.length > 100) {
            return result.substring(0, 97) + '...';
        }
        
        return result || "位置未变化";
    };

    // 渲染变更内容
    const renderChangeContent = (record) => {
        const { event_type, previous_value, new_value } = record;
        
        try {
            switch (event_type) {
                case 'STATUS_CHANGE':
                case 'STATUS_UPDATE':
                    const prevStatus = previous_value?.status || previous_value?.current_status;
                    const newStatus = new_value?.status || new_value?.current_status;
                    if (prevStatus) {
                        return (
                            <Space size={4}>
                                <Tag color={ULD_STATUS_COLORS[prevStatus]} size="small">
                                    {ULD_STATUS_LABELS[prevStatus] || prevStatus}
                                </Tag>
                                <Text type="secondary" style={{ fontSize: '12px' }}>→</Text>
                                <Tag color={ULD_STATUS_COLORS[newStatus]} size="small">
                                    {ULD_STATUS_LABELS[newStatus] || newStatus}
                                </Tag>
                            </Space>
                        );
                    } else {
                        return (
                            <Tag color={ULD_STATUS_COLORS[newStatus]} size="small">
                                {ULD_STATUS_LABELS[newStatus] || newStatus}
                            </Tag>
                        );
                    }
                
                case 'LOC_CHANGE':
                case 'LOCATION_UPDATE':
                    return (
                        <Space direction="vertical" size={2}>
                            {new_value?.location_type && (
                                <Text style={{ fontSize: '12px' }}>
                                    <EnvironmentOutlined style={{ marginRight: 4, fontSize: '11px' }} />
                                    {ULD_LOCATION_TYPE_LABELS[new_value.location_type] || new_value.location_type}
                                </Text>
                            )}
                            {new_value?.airport_iata && (
                                <Text type="secondary" style={{ fontSize: '11px' }}>机场: {new_value.airport_iata}</Text>
                            )}
                            {new_value?.transport_identifier && (
                                <Text type="secondary" style={{ fontSize: '11px' }}>运输: {new_value.transport_identifier}</Text>
                            )}
                        </Space>
                    );
                
                case 'CORE_STATUS_CHANGE':
                    const prevCoreStatus = previous_value?.core_status;
                    const newCoreStatus = new_value?.core_status;
                    const coreStatusNames = {
                        'IN_STOCK': '在库',
                        'IN_TRANSIT': '在途', 
                        'OUT_OF_SERVICE': '停用',
                        'RETIRED': '退役',
                        'LOST': '丢失'
                    };
                    
                    if (prevCoreStatus) {
                        return (
                            <Space size={4}>
                                <Tag color="#1890ff" size="small">
                                    {coreStatusNames[prevCoreStatus] || prevCoreStatus}
                                </Tag>
                                <Text type="secondary" style={{ fontSize: '12px' }}>→</Text>
                                <Tag color="#52c41a" size="small">
                                    {coreStatusNames[newCoreStatus] || newCoreStatus}
                                </Tag>
                            </Space>
                        );
                    } else {
                        return (
                            <Tag color="#52c41a" size="small">
                                {coreStatusNames[newCoreStatus] || newCoreStatus}
                            </Tag>
                        );
                    }
                
                case 'TAG_ADD':
                    const addedTag = new_value?.tag_name || (new_value?.tags && new_value.tags.join(', ')) || '标签';
                    return (
                        <Space>
                            <Text style={{ fontSize: '12px', color: '#52c41a' }}>
                                + {addedTag}
                            </Text>
                        </Space>
                    );
                
                case 'TAG_REMOVE':
                    const removedTag = previous_value?.tag_name || (previous_value?.tags && previous_value.tags.join(', ')) || '标签';
                    return (
                        <Space>
                            <Text style={{ fontSize: '12px', color: '#ff7875' }}>
                                - {removedTag}
                            </Text>
                        </Space>
                    );
                
                case 'TAG_CHANGE':
                case 'TAG_MANAGE':
                    return (
                        <Space direction="vertical" size={2}>
                            {previous_value?.tags && (
                                <Text style={{ fontSize: '11px', color: '#ff7875' }}>
                                    - {previous_value.tags.join(', ')}
                                </Text>
                            )}
                            {new_value?.tags && (
                                <Text style={{ fontSize: '11px', color: '#52c41a' }}>
                                    + {new_value.tags.join(', ')}
                                </Text>
                            )}
                        </Space>
                    );
                
                case 'HOLDER_UPDATE':
                    const prevHolder = previous_value?.holder;
                    const newHolder = new_value?.holder;
                    if (prevHolder) {
                        return (
                            <Space size={4}>
                                <Tag size="small">{prevHolder}</Tag>
                                <Text type="secondary" style={{ fontSize: '12px' }}>→</Text>
                                <Tag color="blue" size="small">{newHolder}</Tag>
                            </Space>
                        );
                    } else {
                        return <Tag color="blue" size="small">{newHolder}</Tag>;
                    }
                
                case 'LOADED':
                    return (
                        <Text style={{ fontSize: '12px', color: '#1890ff' }}>
                            <FlagOutlined style={{ marginRight: 4, fontSize: '11px' }} />
                            装载至 {new_value?.transport_identifier || new_value?.flight_number || '运输工具'}
                        </Text>
                    );
                
                case 'UNLOADED':
                    return (
                        <Text style={{ fontSize: '12px', color: '#2f54eb' }}>
                            <EnvironmentOutlined style={{ marginRight: 4, fontSize: '11px' }} />
                            卸载至 {new_value?.airport_iata || new_value?.location || '目的地'}
                        </Text>
                    );
                
                case 'STOCK_IN':
                    return (
                        <Text style={{ fontSize: '12px', color: '#52c41a' }}>
                            <EnvironmentOutlined style={{ marginRight: 4, fontSize: '11px' }} />
                            入库至 {new_value?.airport_iata}
                        </Text>
                    );
                
                case 'REMARKS_UPDATE':
                    const remarks = new_value?.remarks || record.reason;
                    return (
                        <Tooltip title={remarks} placement="topLeft">
                            <Text type="secondary" ellipsis style={{ fontSize: '12px' }}>
                                备注更新: {remarks || '无内容'}
                            </Text>
                        </Tooltip>
                    );
                
                case 'DELIVERY':
                    const consigneeName = new_value?.consignee_name || record.reason;
                    return (
                        <Text style={{ fontSize: '12px', color: '#fa8c16' }}>
                            <ExportOutlined style={{ marginRight: 4, fontSize: '11px' }} />
                            外放给收货人: {consigneeName || '未知收货人'}
                        </Text>
                    );
                
                case 'DELIVERY_REVOKE':
                    return (
                        <Text style={{ fontSize: '12px', color: '#faad14' }}>
                            <ImportOutlined style={{ marginRight: 4, fontSize: '11px' }} />
                            撤销外放，恢复可调配状态
                        </Text>
                    );
                
                default:
                    // 使用通用格式化逻辑处理复杂变更
                    const formattedContent = formatGenericChangeContent(event_type, previous_value, new_value, record);
                    if (formattedContent && formattedContent !== "位置未变化") {
                        return (
                            <Tooltip title={formattedContent} placement="topLeft">
                                <Text type="secondary" ellipsis style={{ fontSize: '12px' }}>
                                    {formattedContent}
                                </Text>
                            </Tooltip>
                        );
                    } else {
                        // 最后的兜底方案
                        const fallbackContent = record.reason || '无具体描述';
                        return (
                            <Tooltip title={fallbackContent} placement="topLeft">
                                <Text type="secondary" ellipsis style={{ fontSize: '12px' }}>
                                    {fallbackContent}
                                </Text>
                            </Tooltip>
                        );
                    }
            }
        } catch (error) {
            console.warn('渲染变更内容时出错:', error, record);
            // 错误兜底
            const errorFallback = record.reason || '内容解析失败';
            return (
                <Tooltip title={errorFallback} placement="topLeft">
                    <Text type="secondary" ellipsis style={{ fontSize: '12px' }}>
                        {errorFallback}
                    </Text>
                </Tooltip>
            );
        }
    };

    // 表格列定义
    const columns = [
        {
            title: 'ULD识别码',
            dataIndex: 'uld_identifier',
            key: 'uld_identifier',
            width: 110,
            fixed: 'left',
            ellipsis: {
                showTitle: false,
            },
            render: (text) => (
                <Tooltip placement="topLeft" title={text}>
                    <Text strong style={{ color: '#1890ff', fontSize: '12px' }}>{text}</Text>
                </Tooltip>
            ),
        },
        {
            title: '事件类型',
            dataIndex: 'event_type',
            key: 'event_type',
            width: 100,
            render: renderEventType,
        },
        {
            title: '事件时间',
            dataIndex: 'event_timestamp',
            key: 'event_timestamp',
            width: 150,
            render: (dateTime) => {
                if (!dateTime) return '-';
                const date = new Date(dateTime);
                const formattedTime = `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}`;
                
                return (
                    <Tooltip placement="topLeft" title={formattedTime}>
                        <span style={{ fontSize: '12px' }}>{formattedTime}</span>
                    </Tooltip>
                );
            },
        },
        {
            title: '变更内容',
            key: 'change_content',
            width: 200,
            ellipsis: {
                showTitle: false,
            },
            render: (_, record) => renderChangeContent(record),
        },
        {
            title: '关联信息',
            key: 'related_info',
            width: 150,
            ellipsis: {
                showTitle: false,
            },
            render: (_, record) => (
                <Space direction="vertical" size={2}>
                    {record.flight_number && (
                        <Text style={{ fontSize: '12px' }}>
                            <FlagOutlined style={{ marginRight: 4, fontSize: '11px' }} />
                            {record.flight_number}
                        </Text>
                    )}
                    {record.airport_name && (
                        <Text type="secondary" style={{ fontSize: '11px' }}>
                            <EnvironmentOutlined style={{ marginRight: 4, fontSize: '10px' }} />
                            {record.airport_name}
                        </Text>
                    )}
                </Space>
            ),
        },
        {
            title: '操作用户',
            dataIndex: 'user_id',
            key: 'user_id',
            width: 100,
            ellipsis: {
                showTitle: false,
            },
            render: (userId) => {
                if (!userId) return '-';
                
                // 区分系统自动触发和实际用户操作
                const isSystemTriggered = userId === 'System' || userId === 'system' || userId.toLowerCase().includes('system');
                
                return (
                <Tooltip title={userId} placement="topLeft">
                    <Text style={{ fontSize: '12px' }}>
                            {isSystemTriggered ? (
                                <>
                                    <SettingOutlined style={{ 
                                        marginRight: 4, 
                                        fontSize: '11px',
                                        color: '#fa8c16' 
                                    }} />
                                    <Tag color="orange" size="small" style={{ margin: 0, fontSize: '11px' }}>
                                        系统自动
                                    </Tag>
                                </>
                            ) : (
                                <>
                                    <UserOutlined style={{ 
                                        marginRight: 4, 
                                        fontSize: '11px',
                                        color: '#1890ff' 
                                    }} />
                                    <span style={{ color: '#1890ff' }}>{userId}</span>
                                </>
                            )}
                    </Text>
                </Tooltip>
                );
            },
        },
        {
            title: '批次标识',
            dataIndex: 'batch_identifier',
            key: 'batch_identifier',
            width: 120,
            ellipsis: {
                showTitle: false,
            },
            render: (batchIdentifier) => {
                if (!batchIdentifier) {
                    return <Text type="secondary" style={{ fontSize: '12px' }}>-</Text>;
                }
                // 批次号以 UCM- 开头=来自 UCM 入向报文；传了 onUcmLink 则渲染成可回链到来源报文的链接
                const isUcm = typeof batchIdentifier === 'string' && batchIdentifier.startsWith('UCM-');
                if (onUcmLink && isUcm) {
                    return (
                        <Tooltip title="点击查看来源 UCM 报文" placement="topLeft">
                            <Button
                                type="link"
                                size="small"
                                style={{ padding: 0, fontSize: '12px', fontFamily: 'monospace', height: 'auto' }}
                                onClick={() => onUcmLink(batchIdentifier)}
                            >
                                {batchIdentifier}
                            </Button>
                        </Tooltip>
                    );
                }
                return (
                    <Tooltip title={batchIdentifier} placement="topLeft">
                        <Text style={{ fontSize: '12px', fontFamily: 'monospace' }}>
                            {batchIdentifier}
                        </Text>
                    </Tooltip>
                );
            },
        },
        {
            title: '原因',
            dataIndex: 'reason',
            key: 'reason',
            width: 150,
            ellipsis: {
                showTitle: false,
            },
            render: (reason) => (
                <Tooltip title={reason} placement="topLeft">
                    <Text type="secondary" style={{ fontSize: '12px' }}>{reason || '-'}</Text>
                </Tooltip>
            ),
        },
        {
            title: '操作',
            key: 'action',
            width: onUldClick ? 110 : 60,
            fixed: 'right',
            render: (_, record) => (
                <Space size={0}>
                    <Button
                        type="link"
                        size="small"
                        icon={<EyeOutlined style={{ fontSize: '12px' }} />}
                        onClick={() => showDetailModal(record)}
                        style={{ padding: '2px 4px' }}
                    >
                        详情
                    </Button>
                    {onUldClick && (
                        <Button
                            type="link"
                            size="small"
                            onClick={() => onUldClick(record)}
                            style={{ padding: '2px 4px' }}
                        >
                            查看板
                        </Button>
                    )}
                </Space>
            ),
        },
    ].filter((col) => !hiddenColumns.includes(col.key));

    // 渲染详情模态框内容
    const renderDetailContent = () => {
        if (!selectedRecord) return null;

        return (
            <Space direction="vertical" style={{ width: '100%' }} size="large">
                {/* 基本信息 */}
                <Card title="基本信息" size="small">
                    <Descriptions column={2} size="small">
                        <Descriptions.Item label="记录ID">
                            {selectedRecord.id}
                        </Descriptions.Item>
                        <Descriptions.Item label="ULD ID">
                            {selectedRecord.uld_id}
                        </Descriptions.Item>
                        <Descriptions.Item label="ULD识别码">
                            <Text strong>{selectedRecord.uld_identifier}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label="事件类型">
                            {renderEventType(selectedRecord.event_type)}
                        </Descriptions.Item>
                        <Descriptions.Item label="事件时间">
                            <Space>
                                <ClockCircleOutlined />
                                {formatDateTime(selectedRecord.event_timestamp)}
                            </Space>
                        </Descriptions.Item>
                        <Descriptions.Item label="操作用户">
                            <Space>
                                <UserOutlined />
                                {selectedRecord.user_id || '-'}
                            </Space>
                        </Descriptions.Item>
                    </Descriptions>
                </Card>

                {/* 变更详情 */}
                <Card title="变更详情" size="small">
                    <Descriptions column={1} size="small">
                        {/* 用户友好的变更内容展示 */}
                        <Descriptions.Item label="变更内容">
                            <div style={{ padding: '8px 12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                                {renderChangeContent(selectedRecord)}
                            </div>
                        </Descriptions.Item>
                        
                        {/* 格式化的详细变更信息 */}
                        <Descriptions.Item label="详细变更">
                            <div style={{ padding: '8px 12px', backgroundColor: '#fafafa', borderRadius: '4px', fontSize: '13px' }}>
                                {formatGenericChangeContent(selectedRecord.event_type, selectedRecord.previous_value, selectedRecord.new_value, selectedRecord)}
                            </div>
                        </Descriptions.Item>
                        
                        {selectedRecord.reason && (
                            <Descriptions.Item label="变更原因">
                                {selectedRecord.reason}
                            </Descriptions.Item>
                        )}
                        
                        {/* 原始JSON数据（折叠显示） */}
                        <Descriptions.Item label="原始数据">
                            <details style={{ fontSize: '12px' }}>
                                <summary style={{ cursor: 'pointer', color: '#666' }}>点击查看原始JSON数据</summary>
                                <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#f8f8f8', borderRadius: '4px' }}>
                                    {selectedRecord.previous_value && (
                                        <div style={{ marginBottom: '8px' }}>
                                            <Text strong>变更前:</Text>
                                            <pre style={{ margin: '4px 0', fontFamily: 'monospace', fontSize: '11px', lineHeight: '1.4' }}>
                                                {JSON.stringify(selectedRecord.previous_value, null, 2)}
                                            </pre>
                                        </div>
                                    )}
                                    <div>
                                        <Text strong>变更后:</Text>
                                        <pre style={{ margin: '4px 0', fontFamily: 'monospace', fontSize: '11px', lineHeight: '1.4' }}>
                                            {JSON.stringify(selectedRecord.new_value, null, 2)}
                                        </pre>
                                    </div>
                                </div>
                            </details>
                        </Descriptions.Item>
                    </Descriptions>
                </Card>

                {/* 关联信息 */}
                {(selectedRecord.flight_op_id || selectedRecord.flight_number || 
                  selectedRecord.transport_identifier || selectedRecord.airport_iata ||
                  selectedRecord.batch_identifier) && (
                    <Card title="关联信息" size="small">
                        <Descriptions column={2} size="small">
                            {selectedRecord.flight_op_id && (
                                <Descriptions.Item label="航班运行ID">
                                    {selectedRecord.flight_op_id}
                                </Descriptions.Item>
                            )}
                            {selectedRecord.flight_number && (
                                <Descriptions.Item label="航班号">
                                    {selectedRecord.flight_number}
                                </Descriptions.Item>
                            )}
                            {selectedRecord.transport_identifier && (
                                <Descriptions.Item label="运输标识">
                                    {selectedRecord.transport_identifier}
                                </Descriptions.Item>
                            )}
                            {selectedRecord.airport_iata && (
                                <Descriptions.Item label="机场代码">
                                    {selectedRecord.airport_iata}
                                </Descriptions.Item>
                            )}
                            {selectedRecord.airport_name && (
                                <Descriptions.Item label="机场名称">
                                    {selectedRecord.airport_name}
                                </Descriptions.Item>
                            )}
                            {selectedRecord.batch_identifier && (
                                <Descriptions.Item label="批次标识">
                                    {selectedRecord.batch_identifier}
                                </Descriptions.Item>
                            )}
                        </Descriptions>
                    </Card>
                )}

                {/* 系统信息 */}
                <Card title="系统信息" size="small">
                    <Descriptions column={2} size="small">
                        <Descriptions.Item label="创建时间">
                            {formatDateTime(selectedRecord.created_at)}
                        </Descriptions.Item>
                        {selectedRecord.correction_ref_id && (
                            <Descriptions.Item label="校正关联ID">
                                {selectedRecord.correction_ref_id}
                            </Descriptions.Item>
                        )}
                    </Descriptions>
                </Card>
            </Space>
        );
    };

    return (
        <ConfigProvider
            theme={{
                components: {
                    Table: {
                        cellPaddingBlock: 6,
                        cellPaddingInline: 6,
                        fontSize: 12,
                    },
                },
            }}
        >
            <div className="uld-history-table-container">
                <Table
                    columns={columns}
                    dataSource={data}
                    loading={loading}
                    rowKey="id"
                    size="small"
                    pagination={pagination === false ? false : {
                        ...pagination,
                        showSizeChanger: !embedded,
                        showQuickJumper: !embedded,
                        showTotal: (total, range) => 
                            `第 ${range[0]}-${range[1]} 条 / 共 ${total} 条`,
                        pageSizeOptions: ['30', '50', '100', '200'],
                        size: 'small',
                        simple: embedded,
                        onChange: onPageChange,
                        onShowSizeChange,
                    }}
                    scroll={{
                        // 内嵌模式不按整页算高度（会撑爆弹窗/抽屉）；可选传 scrollY 限高，否则随内容
                        x: embedded ? 'max-content' : 1320,
                        ...(embedded ? (scrollY ? { y: scrollY } : {}) : { y: 'calc(100vh - 320px)' }),
                        scrollToFirstRowOnChange: false,
                    }}
                    locale={{
                        emptyText: (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description="暂无历史记录数据"
                            />
                        ),
                    }}
                    sticky={{
                        offsetHeader: 0,
                    }}
                />
            </div>

            <style jsx>{`
                /* ULD历史记录表格容器优化样式 */
                .uld-history-table-container {
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                }
                
                .uld-history-table-container .ant-table-wrapper {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                }
                
                .uld-history-table-container .ant-table {
                    flex: 1;
                }
                
                .uld-history-table-container .ant-table-container {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                }
                
                .uld-history-table-container .ant-table-body {
                    flex: 1;
                    overflow: auto !important;
                }

                /* 确保表格内容可以被选择 */
                .uld-history-table-container .ant-table-tbody > tr > td {
                    user-select: text !important;
                    -webkit-user-select: text !important;
                    -moz-user-select: text !important;
                    -ms-user-select: text !important;
                }
                
                /* 表格行悬停效果 */
                .uld-history-table-container .ant-table-tbody > tr:hover > td {
                    background-color: #f5f5f5 !important;
                }
                
                /* 固定列阴影优化 */
                .uld-history-table-container .ant-table-cell-fix-left,
                .uld-history-table-container .ant-table-cell-fix-right {
                    background: #fff !important;
                }
                
                .uld-history-table-container .ant-table-cell-fix-left-last::after {
                    box-shadow: inset 10px 0 8px -8px rgba(0, 0, 0, 0.06) !important;
                }
                
                .uld-history-table-container .ant-table-cell-fix-right-first::after {
                    box-shadow: inset -10px 0 8px -8px rgba(0, 0, 0, 0.06) !important;
                }
                
                /* 表格滚动条优化 */
                .uld-history-table-container .ant-table-body::-webkit-scrollbar {
                    width: 8px;
                    height: 8px;
                }
                
                .uld-history-table-container .ant-table-body::-webkit-scrollbar-thumb {
                    background-color: rgba(0, 0, 0, 0.2);
                    border-radius: 4px;
                }
                
                .uld-history-table-container .ant-table-body::-webkit-scrollbar-thumb:hover {
                    background-color: rgba(0, 0, 0, 0.3);
                }
                
                .uld-history-table-container .ant-table-body::-webkit-scrollbar-track {
                    background-color: rgba(0, 0, 0, 0.05);
                }
                
                /* 紧凑型表格样式 */
                .uld-history-table-container .ant-table-small .ant-table-thead > tr > th {
                    font-weight: 600;
                    text-align: center;
                    background: #fafafa;
                    border-bottom: 2px solid #f0f0f0;
                    height: 36px;
                    padding: 8px 6px !important;
                    font-size: 12px;
                }
                
                .uld-history-table-container .ant-table-small .ant-table-tbody > tr > td {
                    text-align: center;
                    height: 32px;
                    padding: 6px !important;
                    vertical-align: middle;
                    font-size: 12px;
                }
                
                /* 标签样式优化 */
                .uld-history-table-container .ant-tag {
                    border-radius: 2px;
                    font-size: 11px;
                    line-height: 16px;
                    padding: 0 4px;
                    margin: 1px;
                    height: 18px;
                    display: inline-flex;
                    align-items: center;
                }
                
                /* 操作列居中 */
                .uld-history-table-container .ant-table-tbody > tr > td:last-child {
                    text-align: center !important;
                    padding: 2px !important;
                }
                
                /* ULD识别码列样式 */
                .uld-history-table-container .ant-table-tbody > tr > td:first-child {
                    font-weight: 500;
                    color: #1890ff;
                }
                
                /* 表格分页优化 */
                .uld-history-table-container .ant-table-pagination {
                    margin: 8px 0 0 0 !important;
                    flex-shrink: 0;
                }
                
                /* 响应式优化 */
                @media (max-width: 1400px) {
                    .uld-history-table-container .ant-table-small .ant-table-thead > tr > th,
                    .uld-history-table-container .ant-table-small .ant-table-tbody > tr > td {
                        padding: 4px !important;
                        font-size: 11px;
                    }
                    
                    .uld-history-table-container .ant-tag {
                        font-size: 10px;
                        padding: 0 3px;
                        height: 16px;
                    }
                }
                
                @media (max-width: 1200px) {
                    .uld-history-table-container .ant-table-small .ant-table-thead > tr > th,
                    .uld-history-table-container .ant-table-small .ant-table-tbody > tr > td {
                        padding: 3px !important;
                        font-size: 10px;
                    }
                    
                    .uld-history-table-container .ant-table-small .ant-table-tbody > tr > td {
                        height: 28px;
                    }
                }
            `}</style>

            {/* 详情模态框 */}
            <Modal
                title={`历史记录详情 - ${selectedRecord?.uld_identifier || ''}`}
                open={detailModalVisible}
                onCancel={closeDetailModal}
                footer={[
                    <Button key="close" onClick={closeDetailModal}>
                        关闭
                    </Button>
                ]}
                width={800}
                style={{ top: 20 }}
            >
                {renderDetailContent()}
            </Modal>
        </ConfigProvider>
    );
};

export default UldHistoryTable;
